import * as React from "react"
import { CalendarIcon, ChevronLeftIcon, ChevronRightIcon } from "lucide-react"
import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@/components/ui/popover"

export interface MonthInputProps {
    /** YYYY-MM */
    value?: string
    onChange: (month: string) => void
    /** YYYY-MM inclusive; months after this are disabled */
    max?: string
    /** YYYY-MM inclusive */
    min?: string
    disabled?: boolean
    className?: string
    id?: string
}

function parseMonthValue(value: string | undefined): { year: number; month: number } | null {
    if (!value || !/^\d{4}-\d{2}$/.test(value)) return null
    const [year, month] = value.split("-").map(Number)
    if (month < 1 || month > 12) return null
    return { year, month }
}

function toMonthValue(year: number, month: number): string {
    return `${year}-${String(month).padStart(2, "0")}`
}

function yearNowArt(): number {
    return Number(
        new Intl.DateTimeFormat("en-CA", {
            timeZone: "America/Argentina/Buenos_Aires",
            year: "numeric",
        }).format(new Date()),
    )
}

/** Etiqueta es-AR sin Date local (evita corrimiento por TZ). */
function formatMonthLabel(value: string): string {
    const parsed = parseMonthValue(value)
    if (!parsed) return ""
    const raw = new Intl.DateTimeFormat("es-AR", {
        month: "long",
        year: "numeric",
        timeZone: "UTC",
    }).format(new Date(Date.UTC(parsed.year, parsed.month - 1, 1)))
    return raw.charAt(0).toUpperCase() + raw.slice(1)
}

function monthShortLabel(monthIndex0: number): string {
    const raw = new Intl.DateTimeFormat("es-AR", {
        month: "short",
        timeZone: "UTC",
    }).format(new Date(Date.UTC(2020, monthIndex0, 1)))
    const cleaned = raw.replace(".", "").trim()
    return cleaned.charAt(0).toUpperCase() + cleaned.slice(1)
}

export function MonthInput({
    value,
    onChange,
    max,
    min,
    disabled = false,
    className,
    id,
}: MonthInputProps) {
    const [open, setOpen] = React.useState(false)
    const selected = parseMonthValue(value)
    const maxParsed = parseMonthValue(max)
    const minParsed = parseMonthValue(min)
    const [viewYear, setViewYear] = React.useState(
        () => selected?.year ?? maxParsed?.year ?? yearNowArt(),
    )

    const syncViewYear = React.useCallback(() => {
        setViewYear(selected?.year ?? maxParsed?.year ?? yearNowArt())
    }, [selected?.year, maxParsed?.year])

    React.useEffect(() => {
        if (!open) syncViewYear()
    }, [open, syncViewYear])

    const canPrevYear = !minParsed || viewYear > minParsed.year
    const canNextYear = !maxParsed || viewYear < maxParsed.year

    const isMonthDisabled = (month: number) => {
        const next = toMonthValue(viewYear, month)
        if (max && next > max) return true
        if (min && next < min) return true
        return false
    }

    return (
        <div className={cn("relative flex min-w-[11.5rem] items-center", className)}>
            <Input
                id={id}
                readOnly
                disabled={disabled}
                value={value ? formatMonthLabel(value) : ""}
                placeholder="Mes"
                className="cursor-pointer pr-10"
                aria-label="Elegir mes"
                onClick={() => {
                    if (!disabled) setOpen(true)
                }}
                onKeyDown={(event) => {
                    if (disabled) return
                    if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault()
                        setOpen(true)
                    }
                }}
            />
            <Popover open={open} onOpenChange={setOpen}>
                <PopoverTrigger asChild>
                    <Button
                        type="button"
                        variant="ghost"
                        className="absolute right-0 h-full w-10 p-0 text-muted-foreground hover:bg-transparent"
                        disabled={disabled}
                    >
                        <CalendarIcon className="h-4 w-4" />
                        <span className="sr-only">Abrir calendario de mes</span>
                    </Button>
                </PopoverTrigger>
                <PopoverContent className="z-[100] w-auto overflow-visible p-0" align="end">
                    <div
                        className={cn(
                            "bg-background group/calendar w-fit p-3",
                            "[[data-slot=card-content]_&]:bg-transparent",
                            "[[data-slot=popover-content]_&]:bg-transparent",
                        )}
                    >
                        <div className="relative mb-3 flex h-8 items-center justify-center">
                            <Button
                                type="button"
                                variant="ghost"
                                className="absolute left-0 size-8 p-0"
                                disabled={!canPrevYear}
                                onClick={() => setViewYear((year) => year - 1)}
                                aria-label="Año anterior"
                            >
                                <ChevronLeftIcon className="size-4" />
                            </Button>
                            <span className="text-sm font-medium tabular-nums">{viewYear}</span>
                            <Button
                                type="button"
                                variant="ghost"
                                className="absolute right-0 size-8 p-0"
                                disabled={!canNextYear}
                                onClick={() => setViewYear((year) => year + 1)}
                                aria-label="Año siguiente"
                            >
                                <ChevronRightIcon className="size-4" />
                            </Button>
                        </div>
                        <div className="grid grid-cols-3 gap-1">
                            {Array.from({ length: 12 }, (_, index) => {
                                const month = index + 1
                                const isSelected =
                                    selected?.year === viewYear && selected?.month === month
                                return (
                                    <Button
                                        key={month}
                                        type="button"
                                        variant={isSelected ? "default" : "ghost"}
                                        size="sm"
                                        className="h-8 font-normal"
                                        disabled={isMonthDisabled(month)}
                                        onClick={() => {
                                            const next = toMonthValue(viewYear, month)
                                            onChange(next)
                                            setOpen(false)
                                        }}
                                    >
                                        {monthShortLabel(index)}
                                    </Button>
                                )
                            })}
                        </div>
                    </div>
                </PopoverContent>
            </Popover>
        </div>
    )
}
